import { API, graphqlOperation } from 'aws-amplify';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { captureError } from '../common/sentry';
import {
  addQmComment,
  onAddQmComment,
  onCreateQmRecord,
  onPublishQmLiveUpdate,
  onUpdateQmRecord,
  createQmRecord,
  getQmDashboardSummary,
  getQmRecord,
  listQmRecords,
  updateQmRecord,
  listQmReferenceReasons,
  getQmFeatureFlags,
  setQmFeatureFlags,
  onSetQmFeatureFlags,
  listQmReferenceClosureCodes,
  listQmReferenceLocations,
  listQmReferenceLocationsAdmin,
  createQmReferenceLocation,
  updateQmReferenceLocation,
  archiveQmReferenceLocation,
  listQmSiteInboundWarehouses,
  listQmSiteInboundWarehousesAdmin,
  createQmSiteInboundWarehouse,
  updateQmSiteInboundWarehouse,
  archiveQmSiteInboundWarehouse,
  listQmSiteInboundLocations,
  listQmSiteInboundLocationsAdmin,
  createQmSiteInboundLocation,
  updateQmSiteInboundLocation,
  archiveQmSiteInboundLocation,
  listQmSiteInboundWarehouseUserRolesAdmin,
  setQmSiteInboundWarehouseUserRoles,
  getReceiveManifestQmMatches,
  getUploadUrl,
  listAttachments,
  confirmAttachment,
  onSaveReceiveManifestSnapshot,
  onSaveManifestPartialReceipt,
  onCreateQmReferenceLocation,
  onUpdateQmReferenceLocation,
  onArchiveQmReferenceLocation,
  onCreateQmSiteInboundWarehouse,
  onUpdateQmSiteInboundWarehouse,
  onArchiveQmSiteInboundWarehouse,
  onSetQmSiteInboundWarehouseUserRoles,
  onCreateQmSiteInboundLocation,
  onUpdateQmSiteInboundLocation,
  onArchiveQmSiteInboundLocation,
  onConfirmAttachment
} from '../queries/queries';
import type { ManifestPartialReceiptStatusResult } from './manifestPartialReceipt';

export type QmCrudStatus = 'CREATED' | 'PENDING' | 'CLOSED';
export type QmRecordType = 'INBOUND' | 'SITE_INBOUND';

export type QmStatusHistoryEntry = {
  id: number;
  fromStatus: QmCrudStatus | null;
  toStatus: QmCrudStatus;
  changedBy: string;
  changedAt: string;
  note: string | null;
};

export type QmChangeHistoryEntry = {
  id: number;
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
  changedBy: string;
  changedAt: string;
};

export type QmCommentEntry = {
  id: number;
  body: string;
  createdBy: string;
  createdAt: string;
};

export type QmPoLineDetail = {
  lineIndex: number;
  poLineId: string | null;
  poLine: string | null;
  itemId: string | null;
  linePayload: Record<string, unknown> | null;
  orderedQuantity: number | null;
  receivedQuantity: number | null;
  quantityVariance: number | null;
};

export type QmPackageDetail = {
  packageNumber: number;
  packageType: string | null;
};

export type QmRecord = {
  qmId: number;
  qmNumber: string;
  type: QmRecordType;
  typeLabel: string;
  status: QmCrudStatus;
  reasonCode: string;
  reasonLabel: string;
  closureCode: string | null;
  closureLabel: string | null;
  sscc: string | null;
  asn: string | null;
  tripId: string | null;
  tripReference: string | null;
  manifestId: string | null;
  poNumber: string | null;
  poLine: string | null;
  poLineId: string | null;
  itemId: string | null;
  supplierCode: string | null;
  supplierName: string | null;
  contactName: string | null;
  itemLocation: string;
  itemLocationId: number | null;
  receivingWarehouseId: string | null;
  packageCount: number | null;
  packageType: string | null;
  otherInformation: string | null;
  description: string | null;
  closureNotes: string | null;
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
  closedAt: string | null;
  statusHistory: QmStatusHistoryEntry[];
  changeHistory: QmChangeHistoryEntry[];
  comments: QmCommentEntry[];
  poLines: QmPoLineDetail[];
  packages: QmPackageDetail[];
};

export type QmPoLineDetailInput = {
  poLineId?: string | null;
  poLine?: string | null;
  itemId?: string | null;
  asnLineId?: string | null;
  linePayload?: Record<string, unknown> | string | null;
  orderedQuantity?: number | null;
  receivedQuantity?: number | null;
  quantityVariance?: number | null;
};

export type QmPackageDetailInput = {
  packageNumber: number;
  packageType?: string | null;
};

export type ListQmRecordsFilterInput = {
  status?: QmCrudStatus | null;
  reasonCode?: string | null;
  closureCode?: string | null;
  createdBy?: string | null;
  itemLocation?: string | null;
  supplierName?: string | null;
  poNumber?: string | null;
  qmNumber?: string | null;
  createdFrom?: string | null;
  createdTo?: string | null;
  resolvedFrom?: string | null;
  resolvedTo?: string | null;
  search?: string | null;
};

export type QmRecordPageResult = {
  items: QmRecord[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type QmDashboardSummaryResult = {
  total: number;
  created: number;
  pending: number;
  closed: number;
};

export type ReceiveManifestQmMatchKeyInput = {
  asn: string;
  poLineId: string;
  itemId?: string | null;
};

export type ReceiveManifestQmMatchResult = {
  asn: string;
  poLineId: string;
  itemId?: string | null;
  qmId: number | null;
  qmNumber: string | null;
  status: QmCrudStatus | null;
  reasonCode: string | null;
  reasonLabel: string | null;
  closureCode: string | null;
  closureLabel: string | null;
  createdBy?: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

export type QmFeatureFlagsResult = {
  qmNewDashboardEnabled: boolean;
  qmSiteInboundEnabled: boolean;
};

export type AddQmCommentInput = {
  qmId: number;
  body: string;
  createdBy?: string | null;
};

export type CreateQmRecordInput = {
  type?: QmRecordType | null;
  reasonCode: string;
  sscc?: string | null;
  asn?: string | null;
  tripId?: string | null;
  tripReference?: string | null;
  manifestId?: string | null;
  targetType?: string | null;
  ssccId?: string | null;
  asnId?: string | null;
  asnLineId?: string | null;
  poNumber?: string | null;
  poLine?: string | null;
  poLineId?: string | null;
  itemId?: string | null;
  supplierCode?: string | null;
  supplierName?: string | null;
  contactName?: string | null;
  itemLocation: string;
  itemLocationId?: number | null;
  packageCount?: number | null;
  packageType?: string | null;
  otherInformation?: string | null;
  description?: string | null;
  poLines?: QmPoLineDetailInput[] | null;
  packages?: QmPackageDetailInput[] | null;
  createdBy?: string | null;
};

export type UpdateQmRecordInput = {
  qmId: number;
  status?: QmCrudStatus | null;
  reasonCode?: string | null;
  closureCode?: string | null;
  sscc?: string | null;
  asn?: string | null;
  poNumber?: string | null;
  poLine?: string | null;
  poLineId?: string | null;
  itemId?: string | null;
  supplierCode?: string | null;
  supplierName?: string | null;
  contactName?: string | null;
  itemLocation?: string | null;
  itemLocationId?: number | null;
  packageCount?: number | null;
  packageType?: string | null;
  otherInformation?: string | null;
  description?: string | null;
  closureNotes?: string | null;
  updatedBy?: string | null;
  note?: string | null;
};

type CreateQmRecordMutation = {
  createQmRecord: QmRecord;
};

type UpdateQmRecordMutation = {
  updateQmRecord: QmRecord;
};

type GetQmRecordQuery = {
  getQmRecord: QmRecord;
};

type ListQmRecordsQuery = {
  listQmRecords: QmRecordPageResult;
};

type GetQmDashboardSummaryQuery = {
  getQmDashboardSummary: QmDashboardSummaryResult;
};

type GetReceiveManifestQmMatchesQuery = {
  getReceiveManifestQmMatches: ReceiveManifestQmMatchResult[];
};

type GetQmFeatureFlagsQuery = {
  getQmFeatureFlags: QmFeatureFlagsResult;
};

type SetQmFeatureFlagsMutation = {
  setQmFeatureFlags: QmFeatureFlagsResult;
};

type AddQmCommentMutation = {
  addQmComment: QmRecord;
};

export type QmReasonOption = {
  code: string;
  label: string;
  displayOrder: number;
};

export type QmClosureCodeOption = {
  code: string;
  label: string;
  reasonCode: string;
  displayOrder: number;
};

export type QmLocationOption = {
  code: string;
  label: string;
  displayOrder: number;
};

export type QmLocationAdminOption = {
  code: string;
  label: string;
  displayOrder: number;
  active: boolean;
};

export type CreateQmLocationInput = {
  code: string;
  label: string;
  displayOrder?: number | null;
};

export type UpdateQmLocationInput = {
  code: string;
  label: string;
  displayOrder?: number | null;
};

export type ArchiveQmLocationInput = {
  code: string;
};

type ListQmReferenceReasonsQuery = {
  listQmReferenceReasons: QmReasonOption[];
};

type ListQmReferenceClosureCodesQuery = {
  listQmReferenceClosureCodes: QmClosureCodeOption[];
};

type ListQmReferenceLocationsQuery = {
  listQmReferenceLocations: QmLocationOption[];
};

type ListQmReferenceLocationsAdminQuery = {
  listQmReferenceLocationsAdmin: QmLocationAdminOption[];
};

export type QmSiteInboundLocationOption = {
  id: number;
  receivingWarehouseId: string;
  receivingWarehouseLabel: string | null;
  code: string;
  label: string;
  displayOrder: number;
};

export type QmSiteInboundLocationAdminOption = QmSiteInboundLocationOption & {
  active: boolean;
};

export type CreateQmSiteInboundLocationInput = {
  receivingWarehouseId: string | number;
  code: string;
  label: string;
  displayOrder?: number | null;
};

export type UpdateQmSiteInboundLocationInput = CreateQmSiteInboundLocationInput;

export type ArchiveQmSiteInboundLocationInput = {
  receivingWarehouseId: string | number;
  code: string;
};

type ListQmSiteInboundLocationsQuery = {
  listQmSiteInboundLocations: QmSiteInboundLocationOption[];
};

type ListQmSiteInboundLocationsAdminQuery = {
  listQmSiteInboundLocationsAdmin: QmSiteInboundLocationAdminOption[];
};

export type QmSiteInboundWarehouseOption = {
  receivingWarehouseId: string;
  label: string;
  qmCoordinatorEmails: string[];
  displayOrder: number;
};

export type QmSiteInboundWarehouseAdminOption = QmSiteInboundWarehouseOption & {
  active: boolean;
};

export type CreateQmSiteInboundWarehouseInput = {
  receivingWarehouseId: string | number;
  label: string;
  qmCoordinatorEmails?: string[] | null;
  displayOrder?: number | null;
};

export type UpdateQmSiteInboundWarehouseInput = CreateQmSiteInboundWarehouseInput;

export type ArchiveQmSiteInboundWarehouseInput = {
  receivingWarehouseId: string | number;
};

export type QmSiteInboundWarehouseUserRole = {
  receivingWarehouseId: string;
  receivingWarehouseLabel: string | null;
  userEmail: string;
  userCognitoId: string | null;
  role: string;
};

export type SetQmSiteInboundWarehouseUserRolesInput = {
  userEmail: string;
  userCognitoId?: string | null;
  receivingWarehouseIds: Array<string | number>;
  role?: string | null;
};

type ListQmSiteInboundWarehousesQuery = {
  listQmSiteInboundWarehouses: QmSiteInboundWarehouseOption[];
};

type ListQmSiteInboundWarehousesAdminQuery = {
  listQmSiteInboundWarehousesAdmin: QmSiteInboundWarehouseAdminOption[];
};

type ListQmSiteInboundWarehouseUserRolesAdminQuery = {
  listQmSiteInboundWarehouseUserRolesAdmin: QmSiteInboundWarehouseUserRole[];
};

type CreateQmReferenceLocationMutation = {
  createQmReferenceLocation: QmLocationAdminOption;
};

type UpdateQmReferenceLocationMutation = {
  updateQmReferenceLocation: QmLocationAdminOption;
};

type ArchiveQmReferenceLocationMutation = {
  archiveQmReferenceLocation: QmLocationAdminOption;
};

type CreateQmSiteInboundLocationMutation = {
  createQmSiteInboundLocation: QmSiteInboundLocationAdminOption;
};

type UpdateQmSiteInboundLocationMutation = {
  updateQmSiteInboundLocation: QmSiteInboundLocationAdminOption;
};

type ArchiveQmSiteInboundLocationMutation = {
  archiveQmSiteInboundLocation: QmSiteInboundLocationAdminOption;
};

type CreateQmSiteInboundWarehouseMutation = {
  createQmSiteInboundWarehouse: QmSiteInboundWarehouseAdminOption;
};

type UpdateQmSiteInboundWarehouseMutation = {
  updateQmSiteInboundWarehouse: QmSiteInboundWarehouseAdminOption;
};

type ArchiveQmSiteInboundWarehouseMutation = {
  archiveQmSiteInboundWarehouse: QmSiteInboundWarehouseAdminOption;
};

type SetQmSiteInboundWarehouseUserRolesMutation = {
  setQmSiteInboundWarehouseUserRoles: QmSiteInboundWarehouseUserRole[];
};

function parseAwsJsonObject(value: unknown): Record<string, unknown> | null {
  if (!value) return null;

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }

  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function normalizeQmRecord(record: QmRecord): QmRecord {
  return {
    ...record,
    poLines: (record.poLines || []).map((line) => ({
      ...line,
      linePayload: parseAwsJsonObject(line.linePayload as unknown)
    }))
  };
}

function toGraphQlCreateInput(input: CreateQmRecordInput): Record<string, unknown> {
  return {
    ...input,
    poLines: (input.poLines || []).map((line) => ({
      ...line,
      linePayload:
        line.linePayload === undefined || line.linePayload === null
          ? null
          : typeof line.linePayload === 'string'
            ? line.linePayload
            : JSON.stringify(line.linePayload)
    }))
  };
}

export async function createQmRecordRequest(input: CreateQmRecordInput): Promise<QmRecord> {
  try {
    const graphQlInput = toGraphQlCreateInput(input);
    const result = (await API.graphql(
      graphqlOperation(createQmRecord, { input: graphQlInput })
    )) as GraphQLResult<CreateQmRecordMutation>;

    if (!result.data?.createQmRecord) throw new Error('No data returned from createQmRecord');
    return normalizeQmRecord(result.data.createQmRecord);
  } catch (error) {
    console.error('Error creating QM record:', error);
    captureError(error, 'qmCrud-createQmRecord');
    throw error;
  }
}

export async function updateQmRecordRequest(input: UpdateQmRecordInput): Promise<QmRecord> {
  try {
    const result = (await API.graphql(
      graphqlOperation(updateQmRecord, { input })
    )) as GraphQLResult<UpdateQmRecordMutation>;

    if (!result.data?.updateQmRecord) throw new Error('No data returned from updateQmRecord');
    return normalizeQmRecord(result.data.updateQmRecord);
  } catch (error) {
    console.error('Error updating QM record:', error);
    captureError(error, 'qmCrud-updateQmRecord');
    throw error;
  }
}

export async function getQmRecordRequest({
  qmId,
  qmNumber
}: {
  qmId?: number;
  qmNumber?: string;
}): Promise<QmRecord | null> {
  try {
    const result = (await API.graphql(
      graphqlOperation(getQmRecord, { qmId, qmNumber })
    )) as GraphQLResult<GetQmRecordQuery>;

    return result.data?.getQmRecord ? normalizeQmRecord(result.data.getQmRecord) : null;
  } catch (error) {
    console.error('Error retrieving QM record:', error);
    captureError(error, 'qmCrud-getQmRecord');
    throw error;
  }
}

export async function listQmRecordsRequest(args: {
  page?: number;
  pageSize?: number;
  filter?: ListQmRecordsFilterInput | null;
}): Promise<QmRecordPageResult> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listQmRecords, {
        page: args.page ?? 1,
        pageSize: args.pageSize ?? 10,
        filter: args.filter ?? null
      })
    )) as GraphQLResult<ListQmRecordsQuery>;

    if (!result.data?.listQmRecords) {
      throw new Error('No data returned from listQmRecords');
    }

    return {
      ...result.data.listQmRecords,
      items: (result.data.listQmRecords.items || []).map((item) => normalizeQmRecord(item))
    };
  } catch (error) {
    console.error('Error listing QM records:', error);
    captureError(error, 'qmCrud-listQmRecords');
    throw error;
  }
}

export async function getQmDashboardSummaryRequest(
  filter?: ListQmRecordsFilterInput | null
): Promise<QmDashboardSummaryResult> {
  try {
    const result = (await API.graphql(
      graphqlOperation(getQmDashboardSummary, { filter: filter ?? null })
    )) as GraphQLResult<GetQmDashboardSummaryQuery>;

    if (!result.data?.getQmDashboardSummary) {
      throw new Error('No data returned from getQmDashboardSummary');
    }

    return result.data.getQmDashboardSummary;
  } catch (error) {
    console.error('Error getting QM dashboard summary:', error);
    captureError(error, 'qmCrud-getQmDashboardSummary');
    throw error;
  }
}

export async function getReceiveManifestQmMatchesRequest(
  keys: ReceiveManifestQmMatchKeyInput[]
): Promise<ReceiveManifestQmMatchResult[]> {
  try {
    const normalizedKeys = Array.from(
      new Map(
        (keys || [])
          .map((key) => ({
            asn: String(key?.asn || '').trim(),
            poLineId: String(key?.poLineId || '').trim(),
            itemId: String(key?.itemId || '').trim() || null
          }))
          .filter((key) => Boolean(key.asn) && Boolean(key.poLineId))
          .map((key) => [
            `${key.asn.toLowerCase()}\u001f${key.poLineId.toLowerCase()}\u001f${String(key.itemId || '').toLowerCase()}`,
            key
          ])
      ).values()
    );

    if (normalizedKeys.length === 0) {
      return [];
    }

    const result = (await API.graphql(
      graphqlOperation(getReceiveManifestQmMatches, {
        input: { keys: normalizedKeys }
      })
    )) as GraphQLResult<GetReceiveManifestQmMatchesQuery>;

    return result.data?.getReceiveManifestQmMatches ?? [];
  } catch (error) {
    console.error('Error getting receive-manifest QM matches:', error);
    captureError(error, 'qmCrud-getReceiveManifestQmMatches');
    throw error;
  }
}

export async function getQmFeatureFlagsRequest(): Promise<QmFeatureFlagsResult> {
  try {
    const result = (await API.graphql(graphqlOperation(getQmFeatureFlags))) as GraphQLResult<GetQmFeatureFlagsQuery>;

    if (!result.data?.getQmFeatureFlags) {
      throw new Error('No data returned from getQmFeatureFlags');
    }

    return {
      qmNewDashboardEnabled: result.data.getQmFeatureFlags.qmNewDashboardEnabled,
      qmSiteInboundEnabled: result.data.getQmFeatureFlags.qmSiteInboundEnabled ?? false
    };
  } catch (error) {
    console.error('Error getting QM feature flags:', error);
    captureError(error, 'qmCrud-getQmFeatureFlags');
    throw error;
  }
}

export async function setQmFeatureFlagsRequest(input: QmFeatureFlagsResult): Promise<QmFeatureFlagsResult> {
  try {
    const result = (await API.graphql(
      graphqlOperation(setQmFeatureFlags, { input })
    )) as GraphQLResult<SetQmFeatureFlagsMutation>;

    if (!result.data?.setQmFeatureFlags) {
      throw new Error('No data returned from setQmFeatureFlags');
    }

    return result.data.setQmFeatureFlags;
  } catch (error) {
    console.error('Error setting QM feature flags:', error);
    captureError(error, 'qmCrud-setQmFeatureFlags');
    throw error;
  }
}

export async function addQmCommentRequest(input: AddQmCommentInput): Promise<QmRecord> {
  try {
    const result = (await API.graphql(
      graphqlOperation(addQmComment, { input })
    )) as GraphQLResult<AddQmCommentMutation>;

    if (!result.data?.addQmComment) {
      throw new Error('No data returned from addQmComment');
    }

    return normalizeQmRecord(result.data.addQmComment);
  } catch (error) {
    console.error('Error adding QM comment:', error);
    captureError(error, 'qmCrud-addQmComment');
    throw error;
  }
}

export async function listQmReferenceReasonsRequest(typeCode?: QmRecordType | null): Promise<QmReasonOption[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listQmReferenceReasons, { typeCode })
    )) as GraphQLResult<ListQmReferenceReasonsQuery>;
    return result.data?.listQmReferenceReasons ?? [];
  } catch (error) {
    console.error('Error retrieving QM reasons:', error);
    captureError(error, 'qmCrud-listQmReferenceReasons');
    throw error;
  }
}

export async function listQmReferenceClosureCodesRequest(reasonCode?: string | null): Promise<QmClosureCodeOption[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listQmReferenceClosureCodes, { reasonCode: reasonCode ?? null })
    )) as GraphQLResult<ListQmReferenceClosureCodesQuery>;

    return result.data?.listQmReferenceClosureCodes ?? [];
  } catch (error) {
    console.error('Error retrieving QM closure codes:', error);
    captureError(error, 'qmCrud-listQmReferenceClosureCodes');
    throw error;
  }
}

export async function listQmReferenceLocationsRequest(): Promise<QmLocationOption[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listQmReferenceLocations)
    )) as GraphQLResult<ListQmReferenceLocationsQuery>;

    return result.data?.listQmReferenceLocations ?? [];
  } catch (error) {
    console.error('Error retrieving QM locations:', error);
    captureError(error, 'qmCrud-listQmReferenceLocations');
    throw error;
  }
}

export async function listQmReferenceLocationsAdminRequest(): Promise<QmLocationAdminOption[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listQmReferenceLocationsAdmin)
    )) as GraphQLResult<ListQmReferenceLocationsAdminQuery>;

    return result.data?.listQmReferenceLocationsAdmin ?? [];
  } catch (error) {
    console.error('Error retrieving QM location admin list:', error);
    captureError(error, 'qmCrud-listQmReferenceLocationsAdmin');
    throw error;
  }
}

export async function createQmReferenceLocationRequest(input: CreateQmLocationInput): Promise<QmLocationAdminOption> {
  try {
    const result = (await API.graphql(
      graphqlOperation(createQmReferenceLocation, { input })
    )) as GraphQLResult<CreateQmReferenceLocationMutation>;

    if (!result.data?.createQmReferenceLocation) {
      throw new Error('No data returned from createQmReferenceLocation');
    }

    return result.data.createQmReferenceLocation;
  } catch (error) {
    console.error('Error creating QM location:', error);
    captureError(error, 'qmCrud-createQmReferenceLocation');
    throw error;
  }
}

export async function updateQmReferenceLocationRequest(input: UpdateQmLocationInput): Promise<QmLocationAdminOption> {
  try {
    const result = (await API.graphql(
      graphqlOperation(updateQmReferenceLocation, { input })
    )) as GraphQLResult<UpdateQmReferenceLocationMutation>;

    if (!result.data?.updateQmReferenceLocation) {
      throw new Error('No data returned from updateQmReferenceLocation');
    }

    return result.data.updateQmReferenceLocation;
  } catch (error) {
    console.error('Error updating QM location:', error);
    captureError(error, 'qmCrud-updateQmReferenceLocation');
    throw error;
  }
}

export async function archiveQmReferenceLocationRequest(input: ArchiveQmLocationInput): Promise<QmLocationAdminOption> {
  try {
    const result = (await API.graphql(
      graphqlOperation(archiveQmReferenceLocation, { input })
    )) as GraphQLResult<ArchiveQmReferenceLocationMutation>;

    if (!result.data?.archiveQmReferenceLocation) {
      throw new Error('No data returned from archiveQmReferenceLocation');
    }

    return result.data.archiveQmReferenceLocation;
  } catch (error) {
    console.error('Error archiving QM location:', error);
    captureError(error, 'qmCrud-archiveQmReferenceLocation');
    throw error;
  }
}

export async function listQmSiteInboundWarehousesRequest(): Promise<QmSiteInboundWarehouseOption[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listQmSiteInboundWarehouses)
    )) as GraphQLResult<ListQmSiteInboundWarehousesQuery>;

    return result.data?.listQmSiteInboundWarehouses ?? [];
  } catch (error) {
    console.error('Error retrieving site inbound QM warehouses:', error);
    captureError(error, 'qmCrud-listQmSiteInboundWarehouses');
    throw error;
  }
}

export async function listQmSiteInboundWarehousesAdminRequest(): Promise<QmSiteInboundWarehouseAdminOption[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listQmSiteInboundWarehousesAdmin)
    )) as GraphQLResult<ListQmSiteInboundWarehousesAdminQuery>;

    return result.data?.listQmSiteInboundWarehousesAdmin ?? [];
  } catch (error) {
    console.error('Error retrieving site inbound QM warehouse admin list:', error);
    captureError(error, 'qmCrud-listQmSiteInboundWarehousesAdmin');
    throw error;
  }
}

export async function createQmSiteInboundWarehouseRequest(
  input: CreateQmSiteInboundWarehouseInput
): Promise<QmSiteInboundWarehouseAdminOption> {
  try {
    const result = (await API.graphql(
      graphqlOperation(createQmSiteInboundWarehouse, {
        input: { ...input, receivingWarehouseId: String(input.receivingWarehouseId) }
      })
    )) as GraphQLResult<CreateQmSiteInboundWarehouseMutation>;

    if (!result.data?.createQmSiteInboundWarehouse) {
      throw new Error('No data returned from createQmSiteInboundWarehouse');
    }

    return result.data.createQmSiteInboundWarehouse;
  } catch (error) {
    console.error('Error creating site inbound QM warehouse:', error);
    captureError(error, 'qmCrud-createQmSiteInboundWarehouse');
    throw error;
  }
}

export async function updateQmSiteInboundWarehouseRequest(
  input: UpdateQmSiteInboundWarehouseInput
): Promise<QmSiteInboundWarehouseAdminOption> {
  try {
    const result = (await API.graphql(
      graphqlOperation(updateQmSiteInboundWarehouse, {
        input: { ...input, receivingWarehouseId: String(input.receivingWarehouseId) }
      })
    )) as GraphQLResult<UpdateQmSiteInboundWarehouseMutation>;

    if (!result.data?.updateQmSiteInboundWarehouse) {
      throw new Error('No data returned from updateQmSiteInboundWarehouse');
    }

    return result.data.updateQmSiteInboundWarehouse;
  } catch (error) {
    console.error('Error updating site inbound QM warehouse:', error);
    captureError(error, 'qmCrud-updateQmSiteInboundWarehouse');
    throw error;
  }
}

export async function archiveQmSiteInboundWarehouseRequest(
  input: ArchiveQmSiteInboundWarehouseInput
): Promise<QmSiteInboundWarehouseAdminOption> {
  try {
    const result = (await API.graphql(
      graphqlOperation(archiveQmSiteInboundWarehouse, {
        input: { ...input, receivingWarehouseId: String(input.receivingWarehouseId) }
      })
    )) as GraphQLResult<ArchiveQmSiteInboundWarehouseMutation>;

    if (!result.data?.archiveQmSiteInboundWarehouse) {
      throw new Error('No data returned from archiveQmSiteInboundWarehouse');
    }

    return result.data.archiveQmSiteInboundWarehouse;
  } catch (error) {
    console.error('Error archiving site inbound QM warehouse:', error);
    captureError(error, 'qmCrud-archiveQmSiteInboundWarehouse');
    throw error;
  }
}

export async function listQmSiteInboundWarehouseUserRolesAdminRequest(): Promise<QmSiteInboundWarehouseUserRole[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listQmSiteInboundWarehouseUserRolesAdmin)
    )) as GraphQLResult<ListQmSiteInboundWarehouseUserRolesAdminQuery>;

    return result.data?.listQmSiteInboundWarehouseUserRolesAdmin ?? [];
  } catch (error) {
    console.error('Error retrieving site inbound QM warehouse user roles:', error);
    captureError(error, 'qmCrud-listQmSiteInboundWarehouseUserRolesAdmin');
    throw error;
  }
}

export async function setQmSiteInboundWarehouseUserRolesRequest(
  input: SetQmSiteInboundWarehouseUserRolesInput
): Promise<QmSiteInboundWarehouseUserRole[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(setQmSiteInboundWarehouseUserRoles, {
        input: {
          ...input,
          receivingWarehouseIds: input.receivingWarehouseIds.map(String)
        }
      })
    )) as GraphQLResult<SetQmSiteInboundWarehouseUserRolesMutation>;

    if (!result.data?.setQmSiteInboundWarehouseUserRoles) {
      throw new Error('No data returned from setQmSiteInboundWarehouseUserRoles');
    }

    return result.data.setQmSiteInboundWarehouseUserRoles;
  } catch (error) {
    console.error('Error updating site inbound QM warehouse user roles:', error);
    captureError(error, 'qmCrud-setQmSiteInboundWarehouseUserRoles');
    throw error;
  }
}

export async function listQmSiteInboundLocationsRequest(
  receivingWarehouseId: string | number
): Promise<QmSiteInboundLocationOption[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listQmSiteInboundLocations, { receivingWarehouseId: String(receivingWarehouseId) })
    )) as GraphQLResult<ListQmSiteInboundLocationsQuery>;

    return result.data?.listQmSiteInboundLocations ?? [];
  } catch (error) {
    console.error('Error retrieving site inbound QM locations:', error);
    captureError(error, 'qmCrud-listQmSiteInboundLocations');
    throw error;
  }
}

export async function listQmSiteInboundLocationsAdminRequest(
  receivingWarehouseId?: string | number | null
): Promise<QmSiteInboundLocationAdminOption[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listQmSiteInboundLocationsAdmin, {
        receivingWarehouseId: receivingWarehouseId == null ? null : String(receivingWarehouseId)
      })
    )) as GraphQLResult<ListQmSiteInboundLocationsAdminQuery>;

    return result.data?.listQmSiteInboundLocationsAdmin ?? [];
  } catch (error) {
    console.error('Error retrieving site inbound QM location admin list:', error);
    captureError(error, 'qmCrud-listQmSiteInboundLocationsAdmin');
    throw error;
  }
}

export async function createQmSiteInboundLocationRequest(
  input: CreateQmSiteInboundLocationInput
): Promise<QmSiteInboundLocationAdminOption> {
  try {
    const result = (await API.graphql(
      graphqlOperation(createQmSiteInboundLocation, {
        input: { ...input, receivingWarehouseId: String(input.receivingWarehouseId) }
      })
    )) as GraphQLResult<CreateQmSiteInboundLocationMutation>;

    if (!result.data?.createQmSiteInboundLocation) {
      throw new Error('No data returned from createQmSiteInboundLocation');
    }

    return result.data.createQmSiteInboundLocation;
  } catch (error) {
    console.error('Error creating site inbound QM location:', error);
    captureError(error, 'qmCrud-createQmSiteInboundLocation');
    throw error;
  }
}

export async function updateQmSiteInboundLocationRequest(
  input: UpdateQmSiteInboundLocationInput
): Promise<QmSiteInboundLocationAdminOption> {
  try {
    const result = (await API.graphql(
      graphqlOperation(updateQmSiteInboundLocation, {
        input: { ...input, receivingWarehouseId: String(input.receivingWarehouseId) }
      })
    )) as GraphQLResult<UpdateQmSiteInboundLocationMutation>;

    if (!result.data?.updateQmSiteInboundLocation) {
      throw new Error('No data returned from updateQmSiteInboundLocation');
    }

    return result.data.updateQmSiteInboundLocation;
  } catch (error) {
    console.error('Error updating site inbound QM location:', error);
    captureError(error, 'qmCrud-updateQmSiteInboundLocation');
    throw error;
  }
}

export async function archiveQmSiteInboundLocationRequest(
  input: ArchiveQmSiteInboundLocationInput
): Promise<QmSiteInboundLocationAdminOption> {
  try {
    const result = (await API.graphql(
      graphqlOperation(archiveQmSiteInboundLocation, {
        input: { ...input, receivingWarehouseId: String(input.receivingWarehouseId) }
      })
    )) as GraphQLResult<ArchiveQmSiteInboundLocationMutation>;

    if (!result.data?.archiveQmSiteInboundLocation) {
      throw new Error('No data returned from archiveQmSiteInboundLocation');
    }

    return result.data.archiveQmSiteInboundLocation;
  } catch (error) {
    console.error('Error archiving site inbound QM location:', error);
    captureError(error, 'qmCrud-archiveQmSiteInboundLocation');
    throw error;
  }
}

export type QmAttachment = {
  id: number;
  qmId: number;
  fileName: string;
  mimeType: string | null;
  sizeBytes: number | null;
  uploadedBy: string;
  uploadedAt: string;
  downloadUrl: string;
};

export type QmLiveUpdateType = 'CREATED' | 'UPDATED' | 'COMMENTED';

export type QmLiveUpdateEvent = {
  type: QmLiveUpdateType;
  record: QmRecord;
  source: QmSubscriptionFieldName;
};

type QmSubscriptionFieldName = 'onCreateQmRecord' | 'onUpdateQmRecord' | 'onPublishQmLiveUpdate' | 'onAddQmComment';

type QmSubscriptionPayload = {
  value?: {
    data?: Partial<Record<QmSubscriptionFieldName, QmRecord>>;
  };
};

type QmSubscription = {
  unsubscribe: () => void;
};

type SubscriptionObserver<TPayload> = {
  next: (payload: TPayload) => void;
  error: (error: unknown) => void;
};

type SubscriptionStream<TPayload> = {
  subscribe: (observer: SubscriptionObserver<TPayload>) => QmSubscription;
};

function isSubscriptionStream<TPayload = unknown>(value: unknown): value is SubscriptionStream<TPayload> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'subscribe' in value &&
    typeof (value as { subscribe?: unknown }).subscribe === 'function'
  );
}

export type ConfirmAttachmentInput = {
  qmId: number;
  s3Key: string;
  fileName: string;
  mimeType?: string | null;
  sizeBytes?: number | null;
  uploadedBy?: string | null;
};

type GetUploadUrlQuery = {
  getUploadUrl: { uploadUrl: string; s3Key: string };
};

type ListAttachmentsQuery = {
  listAttachments: QmAttachment[];
};

type ConfirmAttachmentMutation = {
  confirmAttachment: QmAttachment;
};

type QmFeatureFlagsSubscriptionPayload = {
  value?: {
    data?: {
      onSetQmFeatureFlags?: QmFeatureFlagsResult;
    };
  };
};

type ManifestPartialReceiptSubscriptionPayload = {
  value?: {
    data?: {
      onSaveManifestPartialReceipt?: ManifestPartialReceiptStatusResult;
    };
  };
};

type ReceiveManifestSnapshotSubscriptionPayload = {
  value?: {
    data?: {
      onSaveReceiveManifestSnapshot?: string;
    };
  };
};

type QmAttachmentSubscriptionPayload = {
  value?: {
    data?: {
      onConfirmAttachment?: QmAttachment;
    };
  };
};

type QmSettingsSubscriptionFieldName =
  | 'onCreateQmReferenceLocation'
  | 'onUpdateQmReferenceLocation'
  | 'onArchiveQmReferenceLocation'
  | 'onCreateQmSiteInboundWarehouse'
  | 'onUpdateQmSiteInboundWarehouse'
  | 'onArchiveQmSiteInboundWarehouse'
  | 'onSetQmSiteInboundWarehouseUserRoles'
  | 'onCreateQmSiteInboundLocation'
  | 'onUpdateQmSiteInboundLocation'
  | 'onArchiveQmSiteInboundLocation';

export type QmSettingsLiveUpdateEvent =
  | {
      area: 'REFERENCE_LOCATION';
      action: 'CREATED' | 'UPDATED' | 'ARCHIVED';
      record: QmLocationAdminOption;
      source: QmSettingsSubscriptionFieldName;
    }
  | {
      area: 'SITE_INBOUND_WAREHOUSE';
      action: 'CREATED' | 'UPDATED' | 'ARCHIVED';
      record: QmSiteInboundWarehouseAdminOption;
      source: QmSettingsSubscriptionFieldName;
    }
  | {
      area: 'SITE_INBOUND_WAREHOUSE_USER_ROLES';
      action: 'UPDATED';
      record: QmSiteInboundWarehouseUserRole[];
      source: QmSettingsSubscriptionFieldName;
    }
  | {
      area: 'SITE_INBOUND_LOCATION';
      action: 'CREATED' | 'UPDATED' | 'ARCHIVED';
      record: QmSiteInboundLocationAdminOption;
      source: QmSettingsSubscriptionFieldName;
    };

type QmSettingsSubscriptionPayload = {
  value?: {
    data?: Partial<{
      onCreateQmReferenceLocation: QmLocationAdminOption;
      onUpdateQmReferenceLocation: QmLocationAdminOption;
      onArchiveQmReferenceLocation: QmLocationAdminOption;
      onCreateQmSiteInboundWarehouse: QmSiteInboundWarehouseAdminOption;
      onUpdateQmSiteInboundWarehouse: QmSiteInboundWarehouseAdminOption;
      onArchiveQmSiteInboundWarehouse: QmSiteInboundWarehouseAdminOption;
      onSetQmSiteInboundWarehouseUserRoles: QmSiteInboundWarehouseUserRole[];
      onCreateQmSiteInboundLocation: QmSiteInboundLocationAdminOption;
      onUpdateQmSiteInboundLocation: QmSiteInboundLocationAdminOption;
      onArchiveQmSiteInboundLocation: QmSiteInboundLocationAdminOption;
    }>;
  };
};

export async function getUploadUrlRequest(args: {
  qmId: number;
  fileName: string;
  mimeType?: string | null;
}): Promise<{ uploadUrl: string; s3Key: string }> {
  try {
    const result = (await API.graphql(graphqlOperation(getUploadUrl, args))) as GraphQLResult<GetUploadUrlQuery>;

    if (!result.data?.getUploadUrl) throw new Error('No data returned from getUploadUrl');
    return result.data.getUploadUrl;
  } catch (error) {
    console.error('Error getting upload URL:', error);
    captureError(error, 'qmCrud-getUploadUrl');
    throw error;
  }
}

export async function uploadFileToS3(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' }
  });
  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
  }
}

export async function confirmAttachmentRequest(input: ConfirmAttachmentInput): Promise<QmAttachment> {
  try {
    const result = (await API.graphql(
      graphqlOperation(confirmAttachment, { input })
    )) as GraphQLResult<ConfirmAttachmentMutation>;

    if (!result.data?.confirmAttachment) throw new Error('No data returned from confirmAttachment');
    return result.data.confirmAttachment;
  } catch (error) {
    console.error('Error confirming attachment:', error);
    captureError(error, 'qmCrud-confirmAttachment');
    throw error;
  }
}

export async function listAttachmentsRequest(qmId: number): Promise<QmAttachment[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listAttachments, { qmId })
    )) as GraphQLResult<ListAttachmentsQuery>;

    return result.data?.listAttachments ?? [];
  } catch (error) {
    console.error('Error listing attachments:', error);
    captureError(error, 'qmCrud-listAttachments');
    throw error;
  }
}

export function subscribeQmLiveUpdates(onEvent: (event: QmLiveUpdateEvent) => void): () => void {
  const liveSubscriptionDefinitions: Array<{
    type: QmLiveUpdateType;
    query: string;
    fieldName: QmSubscriptionFieldName;
  }> = [
    {
      type: 'CREATED',
      query: onCreateQmRecord,
      fieldName: 'onCreateQmRecord'
    },
    {
      type: 'UPDATED',
      query: onUpdateQmRecord,
      fieldName: 'onUpdateQmRecord'
    },
    {
      type: 'UPDATED',
      query: onPublishQmLiveUpdate,
      fieldName: 'onPublishQmLiveUpdate'
    },
    {
      type: 'COMMENTED',
      query: onAddQmComment,
      fieldName: 'onAddQmComment'
    }
  ];

  let isDisposed = false;
  let reconnectAttempts = 0;
  let reconnectTimeoutId: number | null = null;
  let subscriptions: QmSubscription[] = [];

  const clearReconnectTimer = () => {
    if (reconnectTimeoutId !== null) {
      window.clearTimeout(reconnectTimeoutId);
      reconnectTimeoutId = null;
    }
  };

  const unsubscribeAll = () => {
    subscriptions.forEach((subscription) => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    });
    subscriptions = [];
  };

  const scheduleReconnect = () => {
    if (isDisposed || reconnectTimeoutId !== null) return;

    const delayMs = Math.min(30000, 1000 * 2 ** reconnectAttempts);
    reconnectAttempts += 1;

    reconnectTimeoutId = window.setTimeout(() => {
      reconnectTimeoutId = null;
      startSubscriptions();
    }, delayMs);
  };

  const startSubscriptions = () => {
    if (isDisposed) return;

    unsubscribeAll();

    subscriptions = liveSubscriptionDefinitions
      .map(({ type, query, fieldName }): QmSubscription | null => {
        const stream = API.graphql(graphqlOperation(query));

        if (!isSubscriptionStream<QmSubscriptionPayload>(stream)) {
          const error = new Error(`Expected subscription stream for ${fieldName}`);
          console.error(`QM live update subscription setup error (${fieldName}):`, error);
          captureError(error, `qmCrud-subscribe-${fieldName}-setup`);
          scheduleReconnect();
          return null;
        }

        return stream.subscribe({
          next: (payload) => {
            const record = payload?.value?.data?.[fieldName];
            if (record) {
              reconnectAttempts = 0;
              onEvent({ type, record, source: fieldName });
            }
          },
          error: (error) => {
            console.error(`QM live update subscription error (${fieldName}):`, error);
            captureError(error, `qmCrud-subscribe-${fieldName}`);
            scheduleReconnect();
          }
        });
      })
      .filter((subscription): subscription is QmSubscription => subscription !== null);
  };

  startSubscriptions();

  return () => {
    isDisposed = true;
    clearReconnectTimer();
    unsubscribeAll();
  };
}

export function subscribeQmFeatureFlagUpdates(onEvent: (flags: QmFeatureFlagsResult) => void): () => void {
  const stream = API.graphql(graphqlOperation(onSetQmFeatureFlags));

  if (!isSubscriptionStream<QmFeatureFlagsSubscriptionPayload>(stream)) {
    const error = new Error('Expected subscription stream for onSetQmFeatureFlags');
    console.error('QM feature flag subscription setup error:', error);
    captureError(error, 'qmCrud-subscribeQmFeatureFlagUpdates-setup');
    return () => {};
  }

  const subscription = stream.subscribe({
    next: (payload) => {
      const flags = payload?.value?.data?.onSetQmFeatureFlags;
      if (flags) {
        onEvent(flags);
      }
    },
    error: (error) => {
      console.error('QM feature flag subscription error:', error);
      captureError(error, 'qmCrud-subscribeQmFeatureFlagUpdates');
    }
  });

  return () => {
    if (subscription && typeof subscription.unsubscribe === 'function') {
      subscription.unsubscribe();
    }
  };
}

export function subscribeReceiveManifestSnapshotUpdates(onEvent: (response: string) => void): () => void {
  const stream = API.graphql(graphqlOperation(onSaveReceiveManifestSnapshot));

  if (!isSubscriptionStream<ReceiveManifestSnapshotSubscriptionPayload>(stream)) {
    const error = new Error('Expected subscription stream for onSaveReceiveManifestSnapshot');
    console.error('Receive manifest snapshot subscription setup error:', error);
    captureError(error, 'qmCrud-subscribeReceiveManifestSnapshotUpdates-setup');
    return () => {};
  }

  const subscription = stream.subscribe({
    next: (payload) => {
      const response = payload?.value?.data?.onSaveReceiveManifestSnapshot;
      if (response) {
        onEvent(response);
      }
    },
    error: (error) => {
      console.error('Receive manifest snapshot subscription error:', error);
      captureError(error, 'qmCrud-subscribeReceiveManifestSnapshotUpdates');
    }
  });

  return () => subscription.unsubscribe();
}

export function subscribeManifestPartialReceiptUpdates(
  manifestId: string,
  onEvent: (result: ManifestPartialReceiptStatusResult) => void
): () => void {
  const stream = API.graphql(graphqlOperation(onSaveManifestPartialReceipt));

  if (!isSubscriptionStream<ManifestPartialReceiptSubscriptionPayload>(stream)) {
    const error = new Error('Expected subscription stream for onSaveManifestPartialReceipt');
    console.error('Manifest partial receipt subscription setup error:', error);
    captureError(error, 'qmCrud-subscribeManifestPartialReceiptUpdates-setup');
    return () => {};
  }

  const normalizedManifestId = String(manifestId || '').trim();
  const subscription = stream.subscribe({
    next: (payload) => {
      const result = payload?.value?.data?.onSaveManifestPartialReceipt;
      if (result && (!normalizedManifestId || result.manifestId === normalizedManifestId)) {
        onEvent(result);
      }
    },
    error: (error) => {
      console.error('Manifest partial receipt subscription error:', error);
      captureError(error, 'qmCrud-subscribeManifestPartialReceiptUpdates');
    }
  });

  return () => subscription.unsubscribe();
}

export function subscribeQmAttachmentUpdates(qmId: number, onEvent: (attachment: QmAttachment) => void): () => void {
  const stream = API.graphql(graphqlOperation(onConfirmAttachment));

  if (!isSubscriptionStream<QmAttachmentSubscriptionPayload>(stream)) {
    const error = new Error('Expected subscription stream for onConfirmAttachment');
    console.error('QM attachment subscription setup error:', error);
    captureError(error, 'qmCrud-subscribeQmAttachmentUpdates-setup');
    return () => {};
  }

  const subscription = stream.subscribe({
    next: (payload) => {
      const attachment = payload?.value?.data?.onConfirmAttachment;
      if (attachment && attachment.qmId === qmId) {
        onEvent(attachment);
      }
    },
    error: (error) => {
      console.error('QM attachment subscription error:', error);
      captureError(error, 'qmCrud-subscribeQmAttachmentUpdates');
    }
  });

  return () => subscription.unsubscribe();
}

export function subscribeQmSettingsLiveUpdates(onEvent: (event: QmSettingsLiveUpdateEvent) => void): () => void {
  const definitions: Array<{
    query: string;
    source: QmSettingsSubscriptionFieldName;
    toEvent: (record: unknown) => QmSettingsLiveUpdateEvent | null;
  }> = [
    {
      query: onCreateQmReferenceLocation,
      source: 'onCreateQmReferenceLocation',
      toEvent: (record) => ({
        area: 'REFERENCE_LOCATION',
        action: 'CREATED',
        record: record as QmLocationAdminOption,
        source: 'onCreateQmReferenceLocation'
      })
    },
    {
      query: onUpdateQmReferenceLocation,
      source: 'onUpdateQmReferenceLocation',
      toEvent: (record) => ({
        area: 'REFERENCE_LOCATION',
        action: 'UPDATED',
        record: record as QmLocationAdminOption,
        source: 'onUpdateQmReferenceLocation'
      })
    },
    {
      query: onArchiveQmReferenceLocation,
      source: 'onArchiveQmReferenceLocation',
      toEvent: (record) => ({
        area: 'REFERENCE_LOCATION',
        action: 'ARCHIVED',
        record: record as QmLocationAdminOption,
        source: 'onArchiveQmReferenceLocation'
      })
    },
    {
      query: onCreateQmSiteInboundWarehouse,
      source: 'onCreateQmSiteInboundWarehouse',
      toEvent: (record) => ({
        area: 'SITE_INBOUND_WAREHOUSE',
        action: 'CREATED',
        record: record as QmSiteInboundWarehouseAdminOption,
        source: 'onCreateQmSiteInboundWarehouse'
      })
    },
    {
      query: onUpdateQmSiteInboundWarehouse,
      source: 'onUpdateQmSiteInboundWarehouse',
      toEvent: (record) => ({
        area: 'SITE_INBOUND_WAREHOUSE',
        action: 'UPDATED',
        record: record as QmSiteInboundWarehouseAdminOption,
        source: 'onUpdateQmSiteInboundWarehouse'
      })
    },
    {
      query: onArchiveQmSiteInboundWarehouse,
      source: 'onArchiveQmSiteInboundWarehouse',
      toEvent: (record) => ({
        area: 'SITE_INBOUND_WAREHOUSE',
        action: 'ARCHIVED',
        record: record as QmSiteInboundWarehouseAdminOption,
        source: 'onArchiveQmSiteInboundWarehouse'
      })
    },
    {
      query: onSetQmSiteInboundWarehouseUserRoles,
      source: 'onSetQmSiteInboundWarehouseUserRoles',
      toEvent: (record) => ({
        area: 'SITE_INBOUND_WAREHOUSE_USER_ROLES',
        action: 'UPDATED',
        record: record as QmSiteInboundWarehouseUserRole[],
        source: 'onSetQmSiteInboundWarehouseUserRoles'
      })
    },
    {
      query: onCreateQmSiteInboundLocation,
      source: 'onCreateQmSiteInboundLocation',
      toEvent: (record) => ({
        area: 'SITE_INBOUND_LOCATION',
        action: 'CREATED',
        record: record as QmSiteInboundLocationAdminOption,
        source: 'onCreateQmSiteInboundLocation'
      })
    },
    {
      query: onUpdateQmSiteInboundLocation,
      source: 'onUpdateQmSiteInboundLocation',
      toEvent: (record) => ({
        area: 'SITE_INBOUND_LOCATION',
        action: 'UPDATED',
        record: record as QmSiteInboundLocationAdminOption,
        source: 'onUpdateQmSiteInboundLocation'
      })
    },
    {
      query: onArchiveQmSiteInboundLocation,
      source: 'onArchiveQmSiteInboundLocation',
      toEvent: (record) => ({
        area: 'SITE_INBOUND_LOCATION',
        action: 'ARCHIVED',
        record: record as QmSiteInboundLocationAdminOption,
        source: 'onArchiveQmSiteInboundLocation'
      })
    }
  ];

  const subscriptions = definitions
    .map(({ query, source, toEvent }): QmSubscription | null => {
      const stream = API.graphql(graphqlOperation(query));

      if (!isSubscriptionStream<QmSettingsSubscriptionPayload>(stream)) {
        const error = new Error(`Expected subscription stream for ${source}`);
        console.error(`QM settings subscription setup error (${source}):`, error);
        captureError(error, `qmCrud-subscribeQmSettings-${source}-setup`);
        return null;
      }

      return stream.subscribe({
        next: (payload) => {
          const record = payload?.value?.data?.[source];
          if (record) {
            const event = toEvent(record);
            if (event) {
              onEvent(event);
            }
          }
        },
        error: (error) => {
          console.error(`QM settings subscription error (${source}):`, error);
          captureError(error, `qmCrud-subscribeQmSettings-${source}`);
        }
      });
    })
    .filter((subscription): subscription is QmSubscription => subscription !== null);

  return () => {
    subscriptions.forEach((subscription) => subscription.unsubscribe());
  };
}
